import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Button, Alert, CircularProgress, Typography } from '@mui/material';
import { useToast } from '../../contexts/ToastContext';
import {
  buildLabelRows,
  createAutomationSnapshot,
  fetchChannelAutomationData,
  isAutomationStateDirty,
  isChannelLabelsEmpty,
  parseChannelAutomationData,
  saveChannelAutomationSettings,
  type AutomationSnapshot,
} from '../../services/automationService';
import AutomationChannelSettingsSection from './AutomationChannelSettingsSection';
import AutomationLabelSettingsSection from './AutomationLabelSettingsSection';
import AutomationFirstTimeBanner from './AutomationFirstTimeBanner';
import {
  clearFirstTimeBannerDismissed,
  dismissFirstTimeBanner,
  isFirstTimeBannerDismissed,
} from './automationFirstTimeBannerStorage';
import {
  DEFAULT_CHANNEL_AUTOMATION_SETTINGS,
  getAutomationBannerCopy,
  getAutomationBannerState,
  type ChannelAutomationSettings,
  type LabelAutomationSetting,
  type NoSubtagsEmptyStateCopy,
} from './automationTabConstants';

interface AutomationTabProps {
  channelId?: string;
  channelSubtags?: string[];
  onNavigateToChannelConfig?: () => void;
  onOpenChannelConfigEdit?: () => void;
  noSubtagsEmptyStateCopy?: NoSubtagsEmptyStateCopy;
}

const AutomationTab: React.FC<AutomationTabProps> = ({
  channelId,
  channelSubtags = [],
  onNavigateToChannelConfig,
  onOpenChannelConfigEdit,
  noSubtagsEmptyStateCopy,
}) => {
  const { showSuccess } = useToast();

  // Normalize channelSubtags: backend may return Record<string,string[]> for channels
  // saved with the new per-tag subtag format. Flatten to a unique string[] so all
  // downstream .map() calls (buildLabelRows etc.) receive a valid array.
  const safeChannelSubtags = useMemo<string[]>(() => {
    if (Array.isArray(channelSubtags)) return channelSubtags;
    if (channelSubtags && typeof (channelSubtags as unknown) === 'object') {
      return Array.from(
        new Set(Object.values(channelSubtags as unknown as Record<string, string[]>).flat())
      );
    }
    return [];
  }, [channelSubtags]);

  const [channelSettings, setChannelSettings] = useState<ChannelAutomationSettings>({
    ...DEFAULT_CHANNEL_AUTOMATION_SETTINGS,
  });
  const [labels, setLabels] = useState<LabelAutomationSetting[]>([]);
  const [baseline, setBaseline] = useState<AutomationSnapshot | null>(null);
  const [labelsFromApiEmpty, setLabelsFromApiEmpty] = useState(true);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [pulseFirstRow, setPulseFirstRow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const labelSettingsRef = useRef<HTMLDivElement>(null);
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const masterEnabled = channelSettings.auto_draft_enabled;
  const hasSubtags = safeChannelSubtags.length > 0;

  const isDirty = useMemo(() => {
    if (!baseline) return false;
    return isAutomationStateDirty(baseline, channelSettings, labels);
  }, [baseline, channelSettings, labels]);

  const automationBannerState = useMemo(
    () => getAutomationBannerState(labelsFromApiEmpty, masterEnabled, labels),
    [labelsFromApiEmpty, masterEnabled, labels]
  );

  const automationBannerCopy = useMemo(
    () => getAutomationBannerCopy(automationBannerState),
    [automationBannerState]
  );

  const showAutomationBanner =
    hasSubtags &&
    !bannerDismissed &&
    Boolean(channelId) &&
    hasLoadedOnce;

  const applyLoadedData = useCallback(
    (data: Awaited<ReturnType<typeof fetchChannelAutomationData>>) => {
      setChannelSettings(data.settings);
      setLabels(data.labelRows);
      setBaseline(createAutomationSnapshot(data.settings, data.labelRows));
      const labelsEmpty = isChannelLabelsEmpty(data.labels);
      setLabelsFromApiEmpty(labelsEmpty);
      if (channelId) {
        if (labelsEmpty) {
          clearFirstTimeBannerDismissed(channelId);
          setBannerDismissed(false);
        } else {
          setBannerDismissed(isFirstTimeBannerDismissed(channelId));
        }
      }
      setHasLoadedOnce(true);
      setLoadError(null);
      setSaveError(null);
    },
    [channelId]
  );

  const loadData = useCallback(async () => {
    if (!channelId) {
      const rows = buildLabelRows(safeChannelSubtags, {});
      setLabels(rows);
      setBaseline(createAutomationSnapshot(DEFAULT_CHANNEL_AUTOMATION_SETTINGS, rows));
      setLabelsFromApiEmpty(true);
      setHasLoadedOnce(true);
      return;
    }

    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchChannelAutomationData(channelId, safeChannelSubtags);
      applyLoadedData(data);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load automation settings');
    } finally {
      setLoading(false);
    }
  }, [channelId, safeChannelSubtags, applyLoadedData]);

  useEffect(() => {
    if (channelId) {
      setBannerDismissed(isFirstTimeBannerDismissed(channelId));
    }
  }, [channelId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    return () => {
      if (pulseTimerRef.current) {
        clearTimeout(pulseTimerRef.current);
      }
    };
  }, []);

  const handleConfigureLabels = useCallback(() => {
    labelSettingsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setPulseFirstRow(true);
    if (pulseTimerRef.current) {
      clearTimeout(pulseTimerRef.current);
    }
    pulseTimerRef.current = setTimeout(() => setPulseFirstRow(false), 2500);
  }, []);

  const handleDismissBanner = useCallback(() => {
    if (channelId) {
      dismissFirstTimeBanner(channelId);
    }
    setBannerDismissed(true);
  }, [channelId]);

  const handleSave = async () => {
    if (!channelId || !isDirty) return;

    setSaving(true);
    setSaveError(null);
    try {
      const response = await saveChannelAutomationSettings(channelId, {
        settings: channelSettings,
        labelRows: labels,
      });

      const synced = parseChannelAutomationData(response, safeChannelSubtags);
      setChannelSettings(synced.settings);
      setLabels(synced.labelRows);
      setBaseline(createAutomationSnapshot(synced.settings, synced.labelRows));
      const labelsEmpty = isChannelLabelsEmpty(synced.labels);
      setLabelsFromApiEmpty(labelsEmpty);
      if (channelId && labelsEmpty) {
        clearFirstTimeBannerDismissed(channelId);
        setBannerDismissed(false);
      }
      showSuccess('Automation settings saved');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save automation settings');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChannelConfigEdit = () => {
    onNavigateToChannelConfig?.();
    onOpenChannelConfigEdit?.();
  };

  if (loading && !hasLoadedOnce) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError && channelId && !hasLoadedOnce) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          px: 3,
          gap: 2,
        }}
      >
        <Alert severity="error" sx={{ width: '100%', maxWidth: 480 }}>
          {loadError}
        </Alert>
        <Button variant="contained" onClick={loadData} sx={{ textTransform: 'none' }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 3 }}>
      <Box sx={{ maxWidth: 900, width: '100%' }}>
        {loadError && hasLoadedOnce && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={loadData}>
                Retry
              </Button>
            }
          >
            {loadError}
          </Alert>
        )}

        <Box sx={{ mb: 3, opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
          <AutomationChannelSettingsSection
            settings={channelSettings}
            onChange={(next) => {
              setChannelSettings(next);
              setSaveError(null);
            }}
            disabled={saving}
          />
        </Box>

        {showAutomationBanner && (
          <AutomationFirstTimeBanner
            copy={automationBannerCopy}
            onPrimaryAction={handleConfigureLabels}
            onDismiss={handleDismissBanner}
          />
        )}

        <Box sx={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
          <AutomationLabelSettingsSection
            ref={labelSettingsRef}
            labels={labels}
            onChange={(next) => {
              setLabels(next);
              setSaveError(null);
            }}
            masterEnabled={masterEnabled}
            disabled={saving}
            hasSubtags={hasSubtags}
            onOpenChannelConfigEdit={handleOpenChannelConfigEdit}
            noSubtagsEmptyStateCopy={noSubtagsEmptyStateCopy}
            pulseFirstRow={pulseFirstRow}
          />
        </Box>

        {saveError && (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleSave} disabled={saving}>
                Retry
              </Button>
            }
          >
            Failed to save — {saveError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 3, gap: 1 }}>
          {isDirty && !saving && (
            <Typography variant="caption" color="text.secondary">
              Unsaved changes
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !channelId || !isDirty}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
              py: 1,
              minWidth: 130,
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AutomationTab;
